import { createModel } from "@rematch/core";
import request from "@/api/request";
import type {
	Curve,
	ResearchesByDate,
	ResearchesState,
	ResearchRequest,
	RootModel,
	WellSchema,
} from "@/components/WellLoggingOperations/types";

export const schemaTypes = ["date", "drill", "interval", "alphabet"] as const;
export type SchemaType = (typeof schemaTypes)[number];

export interface SetResearchesPayload {
	wellId: string;
	data: ResearchesByDate[];
}

type SetSchemaPayload = {
	wellId: string;
	data: WellSchema;
};

export const researchesModel = createModel<RootModel>()({
	name: "researches",
	state: {
		schemas: {},
		data: {},
		isLoading: false,
		error: null,
		selectedSchema: "date",
		research_codes: {},
	} as ResearchesState,
	reducers: {
		setResearches(state, payload: SetResearchesPayload) {
			return {
				...state,
				data: { ...state.data, [payload.wellId]: payload.data },
				isLoading: false,
			};
		},
		setSchema(state, payload: SetSchemaPayload) {
			return {
				...state,
				schemas: { ...state.schemas, [payload.wellId]: payload.data },
				isLoading: false,
			};
		},
		setLoading(state) {
			return { ...state, isLoading: true };
		},
		setError(state, err) {
			const message = err?.message ?? String(err);
			return { ...state, error: message, isLoading: false };
		},
		setSchemaType(state, payload: SchemaType) {
			return { ...state, selectedSchema: payload };
		},
		setResearchCodes(
			state,
			payload: { wellId: string; researchCodes: object },
		) {
			return {
				...state,
				research_codes: {
					...state.research_codes,
					[payload.wellId]: payload.researchCodes,
				},
			};
		},
	},

	effects: (dispatch) => ({
		async loadResearches(
			{ wellId, researchIds }: { wellId: string; researchIds: number[] },
			rootState,
		) {
			const isExists =
				wellId in (rootState.researches as ResearchesState).schemas;

			if (!isExists) {
				this.setLoading();

				dispatch.tablets.incrementingCurrentRequests();
				try {
					const body = {
						well: wellId,
						curves: researchIds,
					};
					const response = await request.get(
						`logging/load-research-with-schemas/?params=${JSON.stringify(body)}`,
					);
					const data: ResearchRequest = await response.json();

					const newSchema: WellSchema = {
						drill: [],
						interval: [],
						date: [],
					};

					for (const schemaType of schemaTypes) {
            if (schemaType === "alphabet") continue;
						newSchema[schemaType] = data.schemas[schemaType].map((resByDate) =>
							schemaType === "interval"
								? {
										...resByDate,
										children: resByDate.children.map((resByType) => ({
											...data.logging_data[resByType],
										})),
									}
								: {
										...resByDate,
										children: resByDate.children.map((resByType) => ({
											...resByType,
											children: resByType?.children?.map((id) => ({
												...data.logging_data[id],
												date: resByDate.title,
											})),
										})),
									},
						);
					}

					const researchCodes = data.research_codes;
					//для обратной совместимости(по умолчанию тип date кладется как основной источник исследований)
					//в будущем надо брать данные из state.researches.schemas.date, а не из state.researches.data
					//поле data вообще удалить, перед показом делать этого не стоит чтобы случайно ничего не поломать

					this.setResearches({
						wellId,
						data: newSchema.date,
					});

					this.setSchema({
						wellId,
						data: newSchema,
					});

					this.setResearchCodes({
						wellId,
						researchCodes,
					});

					if (data.selectedResearchesData) {
						const curves = data.selectedResearchesData.reduce(
							(a, c) => ({ ...a, [c.id]: { ...c, columnIndex: 0 } }),
							{} as Record<string, Curve>,
						);
						dispatch.curves.setCurves(curves);
					}
				} catch (error) {
					console.warn("err", error);
					this.setError(error);
				}

				dispatch.tablets.decrementingCurrentRequests();
			}
		},
	}),
});
